import React, { useState } from 'react';

const NicheResearch = () => {
    const [topic, setTopic] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!topic.trim()) return;

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/analysis/niche-research', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ topic: topic.trim() })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to analyze niche opportunity.');
            }
            setResult(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem', color: '#f8fafc' }}>
            {/* Header section */}
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                <div style={{ 
                    display: 'inline-block', 
                    padding: '0.4rem 1rem', 
                    background: 'rgba(99, 102, 241, 0.15)', 
                    border: '1px solid rgba(99, 102, 241, 0.3)', 
                    borderRadius: '20px', 
                    fontSize: '0.85rem', 
                    color: '#818cf8',
                    marginBottom: '0.8rem',
                    fontWeight: 600
                }}>
                    🎯 Phase 2 Differentiation Feature (20 Credits)
                </div>
                <h1 style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: 800, 
                    background: 'linear-gradient(135deg, #a855f7, #6366f1, #3b82f6)', 
                    WebkitBackgroundClip: 'text', 
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem'
                }}>
                    Niche Finder & Topic Opportunity Index
                </h1>
                <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: '650px', margin: '0 auto' }}>
                    Calculate the Niche Opportunity Index (NOI) combining search volume, trend momentum, and competition density to spot lucrative content gaps.
                </p>
            </div>

            {/* Search Input Box */}
            <form onSubmit={handleSearch} style={{ 
                background: 'rgba(15, 23, 42, 0.75)', 
                backdropFilter: 'blur(16px)', 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                borderRadius: '16px', 
                padding: '1.5rem', 
                marginBottom: '2rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)'
            }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="Enter niche or topic (e.g. AI Automation, Personal Finance India, Python Projects)..."
                        style={{
                            flex: 1,
                            minWidth: '280px',
                            background: 'rgba(30, 41, 59, 0.8)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            borderRadius: '10px',
                            padding: '0.85rem 1.2rem',
                            color: '#fff',
                            fontSize: '1rem',
                            outline: 'none'
                        }}
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '0.85rem 2rem',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '1rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)'
                        }}
                    >
                        {loading ? 'Analyzing Niche...' : 'Research Niche (20 Credits)'}
                    </button>
                </div>
            </form>

            {error && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#fca5a5',
                    padding: '1rem 1.5rem',
                    borderRadius: '12px',
                    marginBottom: '2rem'
                }}>
                    ❌ {error}
                </div>
            )}

            {/* Results Display */}
            {result && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {/* Top Score Banner */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.9))',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '20px',
                        padding: '2rem',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '1.5rem',
                        alignItems: 'center'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.9rem', color: '#94a3b8', textTransform: 'uppercase', tracking: '0.05em' }}>Niche Opportunity Index (NOI)</div>
                            <div style={{ fontSize: '4rem', fontWeight: 900, color: '#38bdf8', marginTop: '0.2rem' }}>
                                {result.noiScore}<span style={{ fontSize: '1.5rem', color: '#64748b' }}>/100</span>
                            </div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#a7f3d0', marginTop: '0.3rem' }}>
                                {result.opportunityGrade}
                            </div>
                        </div>

                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '1.5rem' }}>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Search Volume Score</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#f8fafc' }}>{result.metrics.searchVolumeScore}/100</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>~{result.metrics.estimatedMonthlySearches} monthly searches</div>
                        </div>

                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '1.5rem' }}>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Trend Momentum</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#34d399' }}>{result.metrics.trendMomentum}/100</div>
                            <div style={{ fontSize: '0.8rem', color: '#34d399' }}>{result.metrics.trendGrowthPercent} 90-day trajectory</div>
                        </div>

                        <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '1.5rem' }}>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Competition Density</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#fbbf24' }}>{result.metrics.competitionDensity}/100</div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{result.metrics.uniqueDominantChannels} major creators in top 15</div>
                        </div>
                    </div>

                    {/* Subtopic Opportunity Cards */}
                    <div>
                        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, marginBottom: '1rem', color: '#f1f5f9' }}>
                            💡 High-Potential Subtopic Opportunities
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.2rem' }}>
                            {result.subtopicOpportunities.map((op, idx) => (
                                <div key={idx} style={{
                                    background: 'rgba(30, 41, 59, 0.6)',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    borderRadius: '14px',
                                    padding: '1.25rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justify: 'space-between'
                                }}>
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '12px', background: 'rgba(52, 211, 153, 0.15)', color: '#34d399', fontWeight: 700 }}>
                                                NOI Score: {op.opportunityScore}
                                            </span>
                                            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Comp: {op.competition}</span>
                                        </div>
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#f8fafc', marginBottom: '0.5rem' }}>{op.title}</h3>
                                        <p style={{ fontSize: '0.85rem', color: '#cbd5e1', marginBottom: '0.8rem' }}>Format: {op.suggestedFormat}</p>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#64748b', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.5rem' }}>
                                        Est. Search Volume: {op.searchVolume}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NicheResearch;
